import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, TriangleAlert, Trophy, ArrowDownWideNarrow, ArrowUpNarrowWide, Infinity as InfinityIcon } from 'lucide-react';
import { fetchDiasSinIncidentes } from '@/services/api';
import { Skeleton } from '@/components/ui/skeleton';
import { useUserStore } from '@/store/userStore';

/** "Sin accidentes" / "Sin casi accidentes" streak counters per servicio
 * (backlog 31/08: contador de días sin incidente por centro). */

const THRESHOLDS = { rojo: 7, amarillo: 30 };

function colorFor(dias) {
  if (dias == null) return '#22C55E'; // sin incidentes registrados = mejor caso
  if (dias < THRESHOLDS.rojo) return '#EF4444';
  if (dias < THRESHOLDS.amarillo) return '#F59E0B';
  return '#22C55E';
}

function Row({ item, field, rank }) {
  const dias = item[field];
  const color = colorFor(dias);
  return (
    <div className="flex items-center gap-2.5 py-1.5" data-testid={`dias-sin-incidente-row-${item.codigo}`}>
      <span className="text-[10px] font-mono w-4 flex-shrink-0" style={{ color: 'var(--vz-text-muted)' }}>{rank}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate" style={{ color: 'var(--vz-text-primary)' }} title={item.nombre}>{item.nombre}</p>
        <p className="text-[10px] truncate" style={{ color: 'var(--vz-text-muted)' }}>{item.delegacion || item.codigo}</p>
      </div>
      <span
        className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold tabular-nums"
        style={{ background: `${color}18`, color, border: `1px solid ${color}40` }}
      >
        {dias == null ? <><InfinityIcon size={11} /> sin registro</> : `${dias}d`}
      </span>
    </div>
  );
}

function MetricPanel({ title, icon: Icon, field, items, accent }) {
  const [mode, setMode] = useState('alerta'); // 'alerta' | 'record'

  const sorted = useMemo(() => {
    const withVal = items.filter(i => i[field] != null);
    const withoutVal = items.filter(i => i[field] == null);
    if (mode === 'alerta') {
      // Menos días primero (más urgente); los "sin registro" no son urgentes → al final
      return [...withVal.sort((a, b) => a[field] - b[field]), ...withoutVal].slice(0, 6);
    }
    // Récord: "sin registro" es el mejor caso → primero, luego mayor racha
    return [...withoutVal, ...withVal.sort((a, b) => b[field] - a[field])].slice(0, 6);
  }, [items, field, mode]);

  return (
    <div className="flex-1 rounded-xl p-4 min-w-[280px]" style={{ background: 'var(--vz-bg-elevated)', border: `1px solid ${accent}30` }} data-testid={`dias-sin-panel-${field}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="text-xs font-bold flex items-center gap-1.5" style={{ color: accent }}>
          <Icon size={13} /> {title}
        </h4>
        <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--vz-border-subtle)' }}>
          <button
            onClick={() => setMode('alerta')}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium transition-colors"
            style={{ background: mode === 'alerta' ? '#EF444420' : 'transparent', color: mode === 'alerta' ? '#EF4444' : 'var(--vz-text-muted)' }}
            data-testid={`dias-sin-${field}-mode-alerta`}
          >
            <TriangleAlert size={10} /> Alerta
          </button>
          <button
            onClick={() => setMode('record')}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium transition-colors"
            style={{ background: mode === 'record' ? '#22C55E20' : 'transparent', color: mode === 'record' ? '#22C55E' : 'var(--vz-text-muted)' }}
            data-testid={`dias-sin-${field}-mode-record`}
          >
            <Trophy size={10} /> Récord
          </button>
        </div>
      </div>
      {sorted.length === 0 ? (
        <p className="text-[11px] py-4 text-center" style={{ color: 'var(--vz-text-muted)' }}>Sin datos disponibles</p>
      ) : (
        <div className="divide-y" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          {sorted.map((item, i) => <Row key={item.codigo} item={item} field={field} rank={i + 1} />)}
        </div>
      )}
    </div>
  );
}

export function DiasSinIncidentesPanel() {
  const { token } = useUserStore();
  const { data, isLoading } = useQuery({
    queryKey: ['dias-sin-incidentes'],
    queryFn: fetchDiasSinIncidentes,
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col sm:flex-row gap-3" data-testid="dias-sin-incidentes-loading">
        <Skeleton className="h-56 flex-1" /><Skeleton className="h-56 flex-1" />
      </div>
    );
  }

  const items = data?.items || [];
  if (items.length === 0) return null;

  return (
    <div data-testid="dias-sin-incidentes-panel">
      <div className="mb-3">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <ShieldCheck size={14} style={{ color: '#22C55E' }} /> Días sin incidentes por servicio
        </h3>
        <p className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
          Racha desde el último accidente / near miss registrado en cada centro activo · rojo &lt;{THRESHOLDS.rojo}d · ámbar &lt;{THRESHOLDS.amarillo}d · verde ≥{THRESHOLDS.amarillo}d
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <MetricPanel title="Sin Accidentes" icon={ShieldCheck} field="dias_sin_accidente" items={items} accent="#4A9EFF" />
        <MetricPanel title="Sin Casi Accidentes" icon={TriangleAlert} field="dias_sin_nm" items={items} accent="#F97316" />
      </div>
    </div>
  );
}
